import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ProposalBillingItem, ProposalReceipt } from 'app/entities';
import { Proposal } from 'app/entities/proposal';
import { ProposalReceiptDialogComponent } from 'app/main/pages/proposal/proposal-receipt-dialog/proposal-receipt-dialog.component';
import { AlertService } from 'app/services/alert.service';
import { ProposalBillingItemService } from 'app/services/http/proposal-billing-item.service';
import { ProposalReceiptService } from 'app/services/http/proposal-receipt.service';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

interface ReceiptRow {
    item: ProposalBillingItem;
    receipt?: ProposalReceipt;
}

/**
 * ORCA-1971: recibos pela lista de orçamentos. Mostra todas as parcelas (itens financeiros) do orçamento e
 * emite ou abre o recibo de cada uma, sem abrir o orçamento.
 */
@Component({
    selector: 'app-proposal-receipts-dialog',
    templateUrl: './proposal-receipts-dialog.component.html',
    styleUrls: ['./proposal-receipts-dialog.component.scss'],
})
export class ProposalReceiptsDialogComponent implements OnInit {
    proposal: Proposal;
    rows: ReceiptRow[] = [];
    loading = true;
    busy = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) data: { proposal: Proposal },
        public dialogRef: MatDialogRef<ProposalReceiptsDialogComponent>,
        private matDialog: MatDialog,
        private router: Router,
        private alertService: AlertService,
        private billingItemService: ProposalBillingItemService,
        private receiptService: ProposalReceiptService,
    ) {
        this.proposal = data.proposal;
    }

    ngOnInit(): void {
        this.load();
    }

    get pendingReceipts(): ReceiptRow[] {
        return this.rows.filter((row) => this.isPaid(row.item) && !row.receipt);
    }

    isPaid(item: ProposalBillingItem): boolean {
        return item.status === ProposalBillingItem.STATUS_PAID;
    }

    private load(): void {
        const id = this.proposal.id!;
        this.loading = true;
        forkJoin([this.billingItemService.findAll(id), this.receiptService.findAll(id)])
            .pipe(finalize(() => this.loading = false))
            .subscribe(([items, receipts]) => {
                const list = receipts.data || [];
                this.rows = (items.data || []).map((item) => ({
                    item,
                    receipt: list.find((receipt) => receipt.proposalBillingItemId === item.id),
                }));
            }, (error) => {
                this.alertService.error(error);
                this.dialogRef.close();
            });
    }

    emit(row: ReceiptRow): void {
        this.matDialog.open(ProposalReceiptDialogComponent, {
            data: { mode: 'create', item: row.item, proposalTitle: this.proposal.shortDescription },
            panelClass: ['form-dialog', 'medium-form-dialog'],
        }).afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            this.busy = true;
            this.receiptService.create(this.proposal.id!, {
                proposal_billing_item_id: row.item.id,
                issue_date: result.issueDate,
                amount: result.amount,
                payment_reference: result.paymentReference,
                payment_method: result.paymentMethod,
                description: result.description,
                show_signature: result.showSignature ? 1 : 0,
            }).pipe(finalize(() => this.busy = false)).subscribe((receipt) => {
                row.receipt = receipt;
                this.alertService.success(`Recibo nº ${receipt.receiptNumber} emitido.`);
                this.openPdf(receipt);
            }, (error) => this.alertService.error(error));
        });
    }

    /** Todos os pagos ainda sem recibo, com a data de hoje */
    emitAll(): void {
        this.busy = true;
        this.receiptService.bulkReceipts(this.proposal.id!)
            .pipe(finalize(() => this.busy = false))
            .subscribe((result) => {
                this.alertService.success(result.message || 'Recibos emitidos.');
                this.load();
            }, (error) => this.alertService.error(error));
    }

    openPdf(receipt: ProposalReceipt): void {
        const url = this.receiptService.pdfUrl(receipt);
        if (url) {
            window.open(url, '_blank', 'noopener');
            return;
        }
        this.receiptService.pdf(this.proposal.id!, receipt.id!).subscribe((blob) => {
            window.open(URL.createObjectURL(blob), '_blank', 'noopener');
        }, (error) => this.alertService.error(error));
    }

    openProposal(): void {
        this.dialogRef.close();
        this.router.navigate(['propostas', this.proposal.token]);
    }
}
